/**
 * Batch execute multiple contract calls in a single transaction
 *
 * Uses MulticallerWithSender to batch multiple calls together, reducing gas costs
 * and ensuring atomic execution while preserving the original msg.sender for
 * authorization checks.
 *
 * IMPORTANT: This uses MulticallerWithSender (not Multicall3) so that contracts
 * using LibMulticaller.senderOrSigner() will correctly identify the original caller.
 * This is required for privileged operations like resolve/finalize that check
 * if msg.sender is the authorized resolver.
 */
import type { Hash, PublicClient, WalletClient } from "viem"
import { encodeFunctionData } from "viem"
import { simulateContract } from "viem/actions"
import {
	MULTICALLER_WITH_SENDER_ADDRESS,
	multicallerWithSenderAbi,
} from "../../abi/multicallerWithSender"
import { Errors } from "../../errors"
import { createExecutionError } from "../../internal/contracts"
import type { SafeTransaction } from "../../types"

export interface BatchExecuteParams {
	/** Array of encoded transactions to execute */
	transactions: SafeTransaction[]
}

export interface BatchExecuteResult {
	txHash: Hash
}

/**
 * Execute multiple contract calls in a single transaction using MulticallerWithSender
 *
 * Uses MulticallerWithSender which preserves the caller's address for authorization checks.
 * Target contracts using LibMulticaller.senderOrSigner() will correctly identify the
 * original wallet address, not the multicaller contract.
 *
 * @param publicClient - Optional public client for simulation. If provided, the batch
 *                       will be simulated before execution to catch errors early.
 *                       Recommended for admin operations.
 * @param walletClient - Wallet client for signing and sending the transaction
 * @param params - Batch execution parameters
 *
 * @example
 * ```ts
 * import { batchExecute, encodeResolve } from "@breakout/sdk"
 *
 * // With simulation (recommended for admin):
 * const { txHash } = await batchExecute(publicClient, walletClient, {
 *   transactions: [
 *     encodeResolve({ market: "0x1...", winningBucket: 0, ... }),
 *     encodeResolve({ market: "0x2...", winningBucket: 1, ... }),
 *   ],
 * })
 *
 * // Without simulation (faster, but may fail on-chain):
 * const { txHash } = await batchExecute(null, walletClient, { transactions })
 * ```
 */
export async function batchExecute(
	publicClient: PublicClient | null,
	walletClient: WalletClient,
	params: BatchExecuteParams,
): Promise<BatchExecuteResult> {
	// Use walletClient.account directly to preserve LocalAccount signing capability.
	// getAccount() returns a plain Address string, causing viem to fall back to
	// JSON-RPC eth_sendTransaction (unsupported by RPC providers like Alchemy).
	const account = walletClient.account
	if (!account) {
		throw Errors.noWalletConnected()
	}

	if (params.transactions.length === 0) {
		throw Errors.invalidAmount("No transactions to execute")
	}

	// If only one transaction, execute directly (with optional simulation)
	const [singleTx] = params.transactions
	if (params.transactions.length === 1 && singleTx) {
		// Simulate single transaction if publicClient provided
		if (publicClient) {
			try {
				await publicClient.call({
					to: singleTx.to,
					data: singleTx.data,
					value: BigInt(singleTx.value),
					account,
				})
			} catch (error) {
				throw createExecutionError(error, {
					market: singleTx.to,
					functionName: "batchExecute (single)",
				})
			}
		}

		const txHash = await walletClient.sendTransaction({
			to: singleTx.to,
			data: singleTx.data,
			value: BigInt(singleTx.value),
			account,
			chain: walletClient.chain,
		})
		return { txHash }
	}

	// Encode calls for MulticallerWithSender
	// aggregateWithSender(address[] targets, bytes[] data, uint256[] values)
	const targets = params.transactions.map(tx => tx.to)
	const data = params.transactions.map(tx => tx.data)
	const values = params.transactions.map(tx => BigInt(tx.value))

	// Calculate total value to send
	const totalValue = values.reduce((sum, v) => sum + v, 0n)

	// Simulate before executing if publicClient provided
	if (publicClient) {
		try {
			await simulateContract(publicClient, {
				address: MULTICALLER_WITH_SENDER_ADDRESS,
				abi: multicallerWithSenderAbi,
				functionName: "aggregateWithSender",
				args: [targets, data, values],
				account,
				value: totalValue,
			})
		} catch (error) {
			throw createExecutionError(error, {
				functionName: "batchExecute",
				args: { targetCount: targets.length, targets },
			})
		}
	}

	const calldata = encodeFunctionData({
		abi: multicallerWithSenderAbi,
		functionName: "aggregateWithSender",
		args: [targets, data, values],
	})

	const txHash = await walletClient.sendTransaction({
		to: MULTICALLER_WITH_SENDER_ADDRESS,
		data: calldata,
		value: totalValue,
		account,
		chain: walletClient.chain,
	})

	return { txHash }
}
